import {
  Enum_Exhibition_Exhibitionstage,
  ExhibitionProjectFragment,
  ExhibitionProjectStatusFragment
} from '@/gql/generated/graphql'
import { CalendarEvent, getProjectEvent } from '@components/add-to-calendar'
import { GROUP_EXHIBITION_Q4_2024_SLUG } from '@consts/common'
import { formatDate } from '@utils/common'
import { getProjectStatus, ValidStatusType } from '@utils/project'
import { fullExhibitionShowRoute } from '@utils/routes'
import { ShopifyProduct } from '@utils/shopify'
import { iskraShopifyProduct, larsShopifyProduct, zachShopifyProduct } from '../../poetics-of-the-infinite/display'

export const getExhibitionCurrentProject = (
  exhibition?: ExhibitionProjectStatusFragment
): ExhibitionProjectFragment | undefined => {
  if (!exhibition) {
    return undefined
  }
  const slug = exhibition.slug

  if (slug === GROUP_EXHIBITION_Q4_2024_SLUG) {
    return getQ42024CurrentProject(exhibition)
  }
  const project = exhibition.Collections?.[0]?.Collection?.Project ?? undefined
  const additionalProject = exhibition.Collections?.[0]?.AdditionalProjects?.[0]?.Project ?? undefined
  return project ?? additionalProject
}

const getExhibitionProject = (exh: ExhibitionProjectStatusFragment, index: number, isPhysical: boolean) => {
  const project = isPhysical
    ? exh.Collections?.[index]?.AdditionalProjects?.[0]?.Project
    : exh.Collections?.[index]?.Collection?.Project

  return project ?? undefined
}

const getQ42024CurrentProject = (exhibition: ExhibitionProjectStatusFragment) => {
  const iskraDigital = getExhibitionProject(exhibition, 0, false)
  const iskraPhysical = getExhibitionProject(exhibition, 0, true)
  const larsDigital = getExhibitionProject(exhibition, 1, false)
  const larsPhysical = getExhibitionProject(exhibition, 1, true)
  const zachDigital = getExhibitionProject(exhibition, 2, false)
  const zachPhysical = getExhibitionProject(exhibition, 2, true)
  let projectToDisplay: ExhibitionProjectFragment | undefined = iskraPhysical
  const exhibitionStage = exhibition.ExhibitionStage

  switch (exhibitionStage) {
    case Enum_Exhibition_Exhibitionstage.Project_1:
      projectToDisplay = iskraPhysical
      break
    case Enum_Exhibition_Exhibitionstage.Project_2:
      projectToDisplay = iskraDigital
      break
    case Enum_Exhibition_Exhibitionstage.Project_3:
      projectToDisplay = larsPhysical
      break
    case Enum_Exhibition_Exhibitionstage.Project_4:
      projectToDisplay = larsDigital
      break
    case Enum_Exhibition_Exhibitionstage.Project_5:
      projectToDisplay = zachPhysical
      break
    case Enum_Exhibition_Exhibitionstage.Project_6:
      projectToDisplay = zachDigital
      break
    case Enum_Exhibition_Exhibitionstage.Complete:
    default:
      projectToDisplay = undefined
      break
  }

  return projectToDisplay
}

export const getAllExhibitionUpcomingProjects = (
  exhibition: ExhibitionProjectStatusFragment
): ExhibitionProjectFragment[] => {
  const projects = exhibition.Collections?.map((collection) => collection?.Collection?.Project)
  const additionalProjects = exhibition.Collections?.map((collection) =>
    collection?.AdditionalProjects?.map((additionalProject) => additionalProject?.Project)
  )
  const allProjects = [...(projects ?? []), ...(additionalProjects.flat() ?? [])]
  return allProjects
    .sort((a, b) => {
      if (!a?.start_datetime || !b?.start_datetime) return 0
      return new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime()
    })
    .filter((project): project is ExhibitionProjectFragment => project !== null)
    .filter((project) => getProjectStatus(project) === ValidStatusType.upcoming)
}

export const getExhibitionProjectEvent = (
  exhibition: ExhibitionProjectStatusFragment,
  project: ExhibitionProjectFragment
): CalendarEvent => {
  let event = getProjectEvent(project)
  if (isPhysicalProject(exhibition, project)) {
    event = getPhysicalProjectEvent(project, exhibition.slug)
  }

  return event
}

export const getPhysicalProjectEvent = (project: ExhibitionProjectFragment, exhibitionSlug: string): CalendarEvent => {
  const event = getProjectEvent(project)
  event.url = fullExhibitionShowRoute(exhibitionSlug)
  event.description = `${event.url}\n${project.auction_end_time ? `\nEstimated end time: ${formatDate(new Date(project.auction_end_time))}\n` : ''}\n${project.description}`
  return event
}

export const isPhysicalProject = (
  exhibition: ExhibitionProjectStatusFragment,
  project: ExhibitionProjectFragment
): boolean => {
  return getShopifyProduct(exhibition, project) !== undefined
}

export const getShopifyProduct = (
  exhibition: ExhibitionProjectStatusFragment,
  project: ExhibitionProjectFragment
): ShopifyProduct | undefined => {
  if (exhibition.slug === GROUP_EXHIBITION_Q4_2024_SLUG) {
    const iskraPhysical = getExhibitionProject(exhibition, 0, true)
    const larsPhysical = getExhibitionProject(exhibition, 1, true)
    const zachPhysical = getExhibitionProject(exhibition, 2, true)
    if (project.id === iskraPhysical?.id) {
      return iskraShopifyProduct
    }
    if (project.id === larsPhysical?.id) {
      return larsShopifyProduct
    }
    if (project.id === zachPhysical?.id) {
      return zachShopifyProduct
    }
  }
  return undefined
}
